import { ref } from 'vue'
import axiosInstance from '@/axios'
import { sanitizePolicyHtml } from '@/utils/sanitizeHtml'

/**
 * useStorefrontPolicyPage -- read one store's hosted policy page (Privacy or
 * Refund) for the storefront policy route.
 *
 * `GET /api/campaign/getStorefrontPolicy/<storeId>/<kind>` answers
 * `{label, html}`, or a uniform 404 for every "there is no such page" case
 * (unknown store, a store that links to its own URL instead, empty content),
 * so a 404 here is the page's one not-found state.
 *
 * `html` is distributor-authored. The API sanitizes on write and again on
 * read, and this composable sanitizes a THIRD time (`sanitizePolicyHtml`)
 * before handing it to the page, so the only string a v-html sink ever sees
 * has been through the render-side allow-list.
 *
 * Public surface:
 *   const { page, loading, error, fetchPolicy } = useStorefrontPolicyPage()
 *   page:    ref<{label, html}|null>   html is render-ready
 *   loading: ref<boolean>
 *   error:   ref<{status, message}|null>
 *   fetchPolicy(storeId, kind): Promise<void>
 */
export function useStorefrontPolicyPage() {
    const page = ref(null)
    const loading = ref(false)
    const error = ref(null)

    let generation = 0

    const fetchPolicy = async (storeId, kind) => {
        const myGeneration = ++generation
        page.value = null
        error.value = null
        if (!storeId || (kind !== 'privacy' && kind !== 'refund')) {
            error.value = { status: 404, message: 'Page not found.' }
            loading.value = false
            return
        }
        loading.value = true
        try {
            const resp = await axiosInstance.get(
                `/api/campaign/getStorefrontPolicy/${encodeURIComponent(
                    storeId
                )}/${kind}`
            )
            if (myGeneration !== generation) return
            const body = resp?.data || {}
            const html = sanitizePolicyHtml(body.html)
            if (!html) {
                error.value = { status: 404, message: 'Page not found.' }
                return
            }
            page.value = { label: String(body.label || ''), html }
        } catch (err) {
            if (myGeneration !== generation) return
            const status = err?.response?.status ?? 0
            error.value =
                status === 404
                    ? { status, message: 'Page not found.' }
                    : { status, message: 'Failed to load this page.' }
        } finally {
            if (myGeneration === generation) loading.value = false
        }
    }

    return { page, loading, error, fetchPolicy }
}

export default useStorefrontPolicyPage
